import { useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../../utils/dateLocale';
import type { ItineraryDay } from '../../../types';

interface Props {
  days: ItineraryDay[];
  /** Rendered muted and unclickable — typically the day the cards already sit on. */
  disabledDayId?: string | null;
  title: string;
  onPick: (dayId: string) => void;
  onClose: () => void;
}

/**
 * The list of days as a destination picker, for the bulk actions that have no
 * drop target to aim at. Deliberately not a modal: choosing a day is one click
 * and a dialog would make the bulk path slower than editing the cards one by one,
 * which is the whole thing this is meant to replace.
 */
export function DayPickerMenu({ days, disabledDayId, title, onPick, onClose }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    // Deferred: the click that opened the menu is still travelling up, and
    // listening straight away would close it again in the same gesture.
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={title}
      className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-60 max-h-72 overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl py-1.5"
    >
      <p className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-widest text-slate-400">
        {title}
      </p>
      {days.map(day => {
        const disabled = day.id === disabledDayId;
        const stops = day.activities.filter(a => !a.isOrigin).length;
        return (
          <button
            key={day.id}
            role="menuitem"
            type="button"
            disabled={disabled}
            onClick={() => onPick(day.id)}
            className={`w-full text-left px-3 py-2 flex items-baseline gap-2 transition-colors ${
              disabled
                ? 'opacity-40 cursor-default'
                : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
          >
            <span className="text-xs font-semibold text-slate-900 dark:text-slate-100 shrink-0">
              {t('itinerary.dayLabel', { n: day.dayNumber })}
            </span>
            <span className="text-2xs text-slate-500 dark:text-slate-400 truncate">
              {format(parseISO(day.date.slice(0, 10)), 'EEE d MMM', { locale: dateLocale })}
            </span>
            <span className="ml-auto text-2xs text-slate-400 shrink-0 tabular-nums">
              {t('itinerary.stopCount', { count: stops })}
            </span>
          </button>
        );
      })}
    </div>
  );
}
